/**
 * In-game chat input (#833) — the ONLY chat UI that exists.
 *
 * Chat v1 is deliberately floating-text-only: a message is rendered as a
 * floater above the speaker's head (`render/combatText.ts`'s `chat` kind) and
 * then it is gone. There is no chat window, no log, no scrollback, no history.
 * THIS component is not a chat panel — it is a one-line compose box that
 * appears on demand and closes the moment you send or cancel.
 *
 * Opening is NOT this component's job. The `openChat` `GameAction` (default
 * `Enter`, rebindable) is resolved through the live keymap by `InputSurface`,
 * which dispatches `chat.setOpen` — so the open key is never hardcoded here
 * and is conflict-checked against every other binding.
 *
 * Keys while open:
 *   • Enter  → send (or, for blank/whitespace-only input, send nothing) and
 *              close. One key opens and sends, per the issue.
 *   • Escape → close WITHOUT sending. Not requested, added anyway: an open
 *              box with no cancel is a trap, especially given the box
 *              captures every gameplay key while it is up.
 *
 * INPUT CAPTURE is enforced by `InputSurface` reading `state.chatOpen`, NOT by
 * this component swallowing events. That split is deliberate: a focus-based
 * guard stops protecting the player the instant focus drifts, and "my
 * abilities fired while I was typing" is a lethal bug. See `InputSurface`'s
 * `onKeyDown`.
 *
 * #855: this component needs no "is chat enabled" check of its own. `chatOpen`
 * cannot be true while chat is disabled (the reducer refuses it), so a
 * disabled chat renders nothing here by construction — one invariant, enforced
 * in one place. What #855 DOES add here is the sender's optimistic local echo;
 * see `ChatInputProps.onLocalEcho`.
 */

import { useEffect, useRef, type ReactElement } from "react";
import { MAX_CHAT_TEXT_LEN } from "@pit/protocol";
import { useStore } from "../store/context.js";
import { useWsClient } from "../net/WsClientProvider.js";

export interface ChatInputProps {
  /**
   * #855: show the sender their own line, immediately and locally.
   *
   * ⚠️ LOAD-BEARING FOR THE SILENT-REJECT DESIGN. The server may drop a
   * message (slur filter, rate limit) with no reply, and the sender must not
   * be able to tell — so their own echo can NEVER be driven by the server
   * round-trip. It is dispatched here, on send, and `mount.ts` correspondingly
   * ignores the server's echo of the local seat. Blocked and delivered
   * messages therefore produce identical client-visible state for the sender.
   *
   * Optional: `MatchScreen` supplies the renderer-backed floater. Rendering
   * `ChatInput` without it (tests, non-match contexts) simply shows no echo.
   */
  readonly onLocalEcho?: (text: string) => void;
}

export function ChatInput({ onLocalEcho }: ChatInputProps = {}): ReactElement | null {
  const { state, dispatch } = useStore();
  const client = useWsClient();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const open = state.chatOpen;

  // Focus on open so the very next keystroke lands in the box. (Correctness
  // does not depend on this — capture is state-driven, not focus-driven — but
  // without it the player would have to click to type.)
  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  if (!open) return null;

  const close = (): void => {
    dispatch({ type: "chat.setOpen", open: false });
  };

  const send = (): void => {
    const text = inputRef.current?.value ?? "";
    // Whitespace-only sends nothing and just closes. `sendChat` re-applies
    // the same rule (and the length cap) at the socket boundary — this is the
    // UX half, that is the protocol half.
    if (text.trim().length > 0) {
      const sent = client.sendChat(text);
      // #855: optimistic, unconditional local echo of exactly what went on
      // the wire. Never gated on a server response — see `ChatInputProps`.
      if (sent !== null) onLocalEcho?.(sent);
    }
    close();
  };

  return (
    <div
      data-testid="chat-input"
      style={{
        position: "absolute",
        bottom: "22%",
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 8,
        display: "flex",
        alignItems: "center",
        gap: 6,
        padding: "4px 8px",
        background: "rgba(10, 14, 20, 0.82)",
        border: "1px solid #3a4757",
        borderRadius: 6,
      }}
    >
      <span style={{ color: "#7e8fa3", fontSize: 12 }}>Say:</span>
      <input
        ref={inputRef}
        data-testid="chat-input-field"
        type="text"
        // Belt-and-braces with `sendChat`'s own cap: the browser stops the
        // player typing past the protocol limit instead of silently
        // truncating what they wrote at send time.
        maxLength={MAX_CHAT_TEXT_LEN}
        autoComplete="off"
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            send();
            return;
          }
          if (e.key === "Escape") {
            e.preventDefault();
            close();
          }
        }}
        style={{
          width: 320,
          background: "#141c26",
          border: "1px solid #2b3644",
          borderRadius: 4,
          color: "#e6ecf3",
          fontSize: 14,
          padding: "4px 6px",
          outline: "none",
        }}
      />
    </div>
  );
}
